import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Building2, User, Phone, Mail, Key, Bell, Shield, Save, Loader2, Send, CheckCircle, Zap, ExternalLink, Award, Copy, Image as ImageIcon, X, MessageSquare } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useComplianceMode } from '../contexts/ComplianceModeContext';

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [seal, setSeal] = useState(null);
  const [loadingSeal, setLoadingSeal] = useState(false);
  const { refresh: refreshComplianceMode } = useComplianceMode();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
    } catch (error) {
      toast.error('Erro ao carregar definições');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await api.put('/settings', settings);
      setSettings(response.data);
      // Refresh mode so sidebar re-renders immediately after change
      refreshComplianceMode();
      toast.success('Definições guardadas com sucesso');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao guardar definições');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setIsTesting(true);
    try {
      const response = await api.post('/settings/test-email');
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar email de teste');
    } finally {
      setIsTesting(false);
    }
  };

  const [isTestingHubspot, setIsTestingHubspot] = useState(false);
  const handleTestHubspot = async () => {
    setIsTestingHubspot(true);
    try {
      const { data } = await api.post('/leads/hubspot/test-token');
      if (data.ok) {
        toast.success(data.message || 'Token HubSpot válido.');
      } else {
        toast.error(data.message || 'Token HubSpot inválido.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao testar token HubSpot');
    } finally {
      setIsTestingHubspot(false);
    }
  };

  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return (
      <Layout title="Definições">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Definições da Empresa">
      <div className="space-y-6 max-w-4xl" data-testid="settings-container">
        {/* Company Info */}
        <Card className="card-flat">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="h-5 w-5 text-blue-600" />
              Informações da Empresa
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome da Empresa</Label>
                <Input
                  value={settings?.company_name || ''}
                  onChange={(e) => updateField('company_name', e.target.value)}
                  placeholder="Nome da empresa"
                  data-testid="company-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>NIF</Label>
                <Input
                  value={settings?.company_nif || ''}
                  onChange={(e) => updateField('company_nif', e.target.value)}
                  placeholder="123456789"
                />
              </div>
              <div className="space-y-2">
                <Label>Setor NIS2</Label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-input bg-background"
                  value={settings?.sector || ''}
                  onChange={(e) => updateField('sector', e.target.value)}
                >
                  <option value="">Selecionar...</option>
                  <option value="essencial">Entidade Essencial</option>
                  <option value="importante">Entidade Importante</option>
                </select>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label className="flex items-center gap-2">
                  Modo de Conformidade
                  <span className="text-[10px] font-normal text-slate-400 uppercase tracking-widest">Feature flag por tenant</span>
                </Label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-input bg-background"
                  value={settings?.compliance_mode || 'both'}
                  onChange={(e) => updateField('compliance_mode', e.target.value)}
                  data-testid="settings-compliance-mode"
                >
                  <option value="both">NIS2 + EU AI Act (padrão · cobertura total)</option>
                  <option value="nis2_only">Apenas NIS2 (Diretiva UE 2022/2555 + Lei 125/2025)</option>
                  <option value="ai_act_only">Apenas EU AI Act (Regulamento UE 2024/1689)</option>
                </select>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Controla que módulos são visíveis para toda a organização. As alterações são aplicadas de imediato após guardar.
                  Escolhe <strong>Apenas EU AI Act</strong> se a tua empresa não está abrangida pela NIS2 mas opera sistemas IA regulados.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Custom Branding (certificate PDF logo) */}
        <Card className="card-flat" data-testid="branding-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ImageIcon className="h-5 w-5 text-indigo-600" />
              Logotipo dos certificados
            </CardTitle>
            <CardDescription>
              O logotipo aparece no topo dos certificados PDF emitidos pela plataforma. PNG ou JPEG, máx. 500 KB.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-4">
              <div className="w-32 h-20 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                {settings?.certificate_logo_base64 ? (
                  <img
                    src={`data:${settings?.certificate_logo_mime || 'image/png'};base64,${settings.certificate_logo_base64}`}
                    alt="Logo"
                    className="max-w-full max-h-full object-contain"
                    data-testid="branding-logo-preview"
                  />
                ) : (
                  <span className="text-xs text-slate-400">Sem logo</span>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  data-testid="branding-logo-file"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 500 * 1024) {
                      toast.error('Ficheiro demasiado grande (máx 500 KB).');
                      return;
                    }
                    const reader = new FileReader();
                    reader.onload = (ev) => {
                      const dataUri = ev.target.result;
                      const commaIdx = dataUri.indexOf(',');
                      const b64 = dataUri.slice(commaIdx + 1);
                      updateField('certificate_logo_base64', b64);
                      updateField('certificate_logo_mime', file.type || 'image/png');
                    };
                    reader.readAsDataURL(file);
                  }}
                  className="text-sm"
                />
                {settings?.certificate_logo_base64 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { updateField('certificate_logo_base64', ''); updateField('certificate_logo_mime', ''); }}
                    data-testid="branding-logo-remove"
                    className="text-red-600 hover:bg-red-50"
                  >
                    <X className="h-3.5 w-3.5 mr-1" /> Remover logo
                  </Button>
                )}
                <p className="text-xs text-slate-500">
                  Para melhores resultados, use um logo horizontal em PNG com fundo transparente. Guarde as definições para aplicar imediatamente aos próximos certificados emitidos.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CISO Contact */}
        <Card className="card-flat">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="h-5 w-5 text-red-600" />
              Contacto CISO
            </CardTitle>
            <CardDescription>Chief Information Security Officer - Responsável pela cibersegurança</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><User className="h-4 w-4" /> Nome</Label>
                <Input
                  value={settings?.ciso_name || ''}
                  onChange={(e) => updateField('ciso_name', e.target.value)}
                  placeholder="João Silva"
                  data-testid="ciso-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Mail className="h-4 w-4" /> Email</Label>
                <Input
                  type="email"
                  value={settings?.ciso_email || ''}
                  onChange={(e) => updateField('ciso_email', e.target.value)}
                  placeholder="ciso@empresa.pt"
                  data-testid="ciso-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Phone className="h-4 w-4" /> Telefone</Label>
                <Input
                  value={settings?.ciso_phone || ''}
                  onChange={(e) => updateField('ciso_phone', e.target.value)}
                  placeholder="+351 912 345 678"
                  data-testid="ciso-phone-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* SPOC Contact */}
        <Card className="card-flat">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="h-5 w-5 text-orange-600" />
              Contacto SPOC
            </CardTitle>
            <CardDescription>Single Point of Contact - Ponto de contacto com autoridades (CNCS)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><User className="h-4 w-4" /> Nome</Label>
                <Input
                  value={settings?.spoc_name || ''}
                  onChange={(e) => updateField('spoc_name', e.target.value)}
                  placeholder="Maria Santos"
                  data-testid="spoc-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Mail className="h-4 w-4" /> Email</Label>
                <Input
                  type="email"
                  value={settings?.spoc_email || ''}
                  onChange={(e) => updateField('spoc_email', e.target.value)}
                  placeholder="spoc@empresa.pt"
                  data-testid="spoc-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Phone className="h-4 w-4" /> Telefone</Label>
                <Input
                  value={settings?.spoc_phone || ''}
                  onChange={(e) => updateField('spoc_phone', e.target.value)}
                  placeholder="+351 912 345 679"
                  data-testid="spoc-phone-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* DPO Contact (RGPD Art. 37) */}
        <Card className="card-flat" data-testid="dpo-contact-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="h-5 w-5 text-purple-600" />
              Contacto DPO (RGPD Art. 37)
            </CardTitle>
            <CardDescription>Encarregado da Proteção de Dados — recebe alertas automáticos de revisão de DPIA e ROPA</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><User className="h-4 w-4" /> Nome</Label>
                <Input
                  value={settings?.dpo_name || ''}
                  onChange={(e) => updateField('dpo_name', e.target.value)}
                  placeholder="Ana Ferreira"
                  data-testid="dpo-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Mail className="h-4 w-4" /> Email</Label>
                <Input
                  type="email"
                  value={settings?.dpo_email || ''}
                  onChange={(e) => updateField('dpo_email', e.target.value)}
                  placeholder="dpo@empresa.pt"
                  data-testid="dpo-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Phone className="h-4 w-4" /> Telefone</Label>
                <Input
                  value={settings?.dpo_phone || ''}
                  onChange={(e) => updateField('dpo_phone', e.target.value)}
                  placeholder="+351 912 345 680"
                  data-testid="dpo-phone-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Email Configuration */}
        <Card className="card-flat">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Key className="h-5 w-5 text-purple-600" />
              Configuração de Email (Resend)
            </CardTitle>
            <CardDescription>API Key para envio de notificações por email</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>API Key do Resend</Label>
              <div className="flex gap-2">
                <Input
                  type="password"
                  value={settings?.resend_api_key || ''}
                  onChange={(e) => updateField('resend_api_key', e.target.value)}
                  placeholder="re_xxxxxxxxxxxxxxxx"
                  className="font-mono"
                  data-testid="resend-api-key-input"
                />
                <Button variant="outline" onClick={handleTestEmail} disabled={isTesting}>
                  {isTesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  <span className="ml-2 hidden sm:inline">Testar</span>
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                Obtenha a sua API Key em <a href="https://resend.com" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">resend.com</a>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* HubSpot CRM Integration */}
        <Card className="card-flat" data-testid="hubspot-config-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Zap className="h-5 w-5 text-orange-600" />
              Integração HubSpot CRM
            </CardTitle>
            <CardDescription>
              Sincroniza automaticamente os leads capturados via <span className="font-medium">/landing/ai-act</span> e <span className="font-medium">/landing/nis2</span> para o teu HubSpot.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="font-medium">Integração ativa</p>
                <p className="text-sm text-slate-500">Quando desativada, nenhum lead é enviado para o HubSpot.</p>
              </div>
              <Switch
                checked={settings?.hubspot_enabled || false}
                onCheckedChange={(v) => updateField('hubspot_enabled', v)}
                data-testid="hubspot-enabled-switch"
              />
            </div>

            <div className="space-y-2">
              <Label>Private App Access Token</Label>
              <div className="flex gap-2">
                <Input
                  type="password"
                  value={settings?.hubspot_access_token || ''}
                  onChange={(e) => updateField('hubspot_access_token', e.target.value)}
                  placeholder="pat-eu1-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  className="font-mono text-xs"
                  data-testid="hubspot-token-input"
                  disabled={!settings?.hubspot_enabled}
                />
                <Button
                  variant="outline"
                  onClick={handleTestHubspot}
                  disabled={isTestingHubspot || !settings?.hubspot_enabled || !settings?.hubspot_access_token}
                  data-testid="hubspot-test-btn"
                >
                  {isTestingHubspot ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                  <span className="ml-2 hidden sm:inline">Testar</span>
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                Cria uma Private App em <a href="https://app.hubspot.com/private-apps" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">HubSpot Settings → Integrations → Private Apps <ExternalLink className="h-3 w-3" /></a> com scopes: <code className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded">crm.objects.contacts.read</code> + <code className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded">crm.objects.contacts.write</code>.
              </p>
              {settings?.hubspot_access_token?.includes('...') && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                  Token guardado (mascarado). Introduz um novo token para substituir.
                </p>
              )}
            </div>

            <Separator />

            <div className="flex items-center justify-between py-2">
              <div>
                <p className="font-medium">Auto-sync de leads</p>
                <p className="text-sm text-slate-500">Envia cada lead capturado nas landings para o HubSpot em segundo plano.</p>
              </div>
              <Switch
                checked={settings?.hubspot_auto_sync_leads !== false}
                onCheckedChange={(v) => updateField('hubspot_auto_sync_leads', v)}
                data-testid="hubspot-autosync-switch"
                disabled={!settings?.hubspot_enabled}
              />
            </div>

            <div className="space-y-2">
              <Label>Email do owner (opcional)</Label>
              <Input
                type="email"
                value={settings?.hubspot_owner_email || ''}
                onChange={(e) => updateField('hubspot_owner_email', e.target.value)}
                placeholder="vendas@empresa.pt"
                data-testid="hubspot-owner-input"
                disabled={!settings?.hubspot_enabled}
              />
              <p className="text-xs text-slate-500">Se preenchido, os contactos criados serão atribuídos a este utilizador no HubSpot (por implementar).</p>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600">
              <p className="font-semibold text-slate-800 mb-1">Propriedades personalizadas escritas no HubSpot:</p>
              <ul className="list-disc list-inside space-y-0.5 text-slate-600">
                <li><code className="text-[10px]">nis2ai_score</code> — pontuação de exposição 0-100</li>
                <li><code className="text-[10px]">nis2ai_level</code> — critical / high / medium / low</li>
                <li><code className="text-[10px]">nis2ai_source</code> — public_landing_ai_act ou public_landing_nis2</li>
                <li><code className="text-[10px]">nis2ai_classification</code> — apenas NIS2 (essencial/importante)</li>
              </ul>
              <p className="mt-1.5 text-slate-500">Cria estas propriedades em HubSpot → Settings → Data Management → Properties → Contact properties.</p>
            </div>
          </CardContent>
        </Card>

        {/* Slack / Microsoft Teams Webhooks */}
        <Card className="card-flat">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-purple-600" />
              Notificações em Chat (Slack / Teams)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">
              Envia mensagens automáticas para o teu canal empresarial quando: revisões são assinadas, checklists estão parcialmente completas ou uma política sofre alterações materiais.
            </p>
            <div className="flex items-center justify-between py-2 border-y border-slate-100">
              <div>
                <p className="font-medium">Notificações webhook ativas</p>
                <p className="text-xs text-slate-500">Desliga para pausar sem apagar URLs</p>
              </div>
              <Switch
                checked={settings?.webhook_notifications_enabled ?? true}
                onCheckedChange={(v) => updateField('webhook_notifications_enabled', v)}
                data-testid="webhooks-enabled-switch"
              />
            </div>
            <div className="space-y-2">
              <Label>Slack Incoming Webhook URL</Label>
              <Input
                type="url"
                placeholder="https://hooks.slack.com/services/..."
                value={settings?.slack_webhook_url || ''}
                onChange={(e) => updateField('slack_webhook_url', e.target.value)}
                data-testid="slack-webhook-url"
              />
              <p className="text-[11px] text-slate-500">
                Cria em <a href="https://api.slack.com/messaging/webhooks" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">api.slack.com → Incoming Webhooks</a> e escolhe o canal do CISO.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Microsoft Teams Webhook URL</Label>
              <Input
                type="url"
                placeholder="https://empresa.webhook.office.com/webhookb2/..."
                value={settings?.teams_webhook_url || ''}
                onChange={(e) => updateField('teams_webhook_url', e.target.value)}
                data-testid="teams-webhook-url"
              />
              <p className="text-[11px] text-slate-500">
                No canal Teams: <em>Conectores → Incoming Webhook → Configurar</em>. Copia o URL gerado.
              </p>
            </div>
            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const r = await api.post('/settings/webhooks/test');
                    const parts = [];
                    if (r.data.slack_configured) parts.push(`Slack: ${r.data.slack_ok ? '✓' : '✗'}`);
                    if (r.data.teams_configured) parts.push(`Teams: ${r.data.teams_ok ? '✓' : '✗'}`);
                    if (parts.length === 0) toast.info('Nenhum webhook configurado. Guarda o URL primeiro.');
                    else toast.success(`Teste enviado · ${parts.join(' · ')}`);
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || 'Falha ao testar webhooks.');
                  }
                }}
                data-testid="webhooks-test-btn"
              >
                Enviar teste
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Notification Settings */}
        <Card className="card-flat">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Bell className="h-5 w-5 text-green-600" />
              Configurações de Notificações
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="font-medium">Notificações Ativas</p>
                <p className="text-sm text-slate-500">Ativar/desativar todas as notificações</p>
              </div>
              <Switch
                checked={settings?.notifications_enabled}
                onCheckedChange={(v) => updateField('notifications_enabled', v)}
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="font-medium">Alertas NIS2 24 Horas</p>
                <p className="text-sm text-slate-500">Notificar incidentes com prazo de 24h</p>
              </div>
              <Switch
                checked={settings?.notify_24h_alerts}
                onCheckedChange={(v) => updateField('notify_24h_alerts', v)}
                disabled={!settings?.notifications_enabled}
              />
            </div>
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="font-medium">Alertas NIS2 72 Horas</p>
                <p className="text-sm text-slate-500">Notificar incidentes com prazo de 72h</p>
              </div>
              <Switch
                checked={settings?.notify_72h_alerts}
                onCheckedChange={(v) => updateField('notify_72h_alerts', v)}
                disabled={!settings?.notifications_enabled}
              />
            </div>
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="font-medium">Tarefas em Atraso</p>
                <p className="text-sm text-slate-500">Notificar tarefas com prazo expirado</p>
              </div>
              <Switch
                checked={settings?.notify_task_overdue}
                onCheckedChange={(v) => updateField('notify_task_overdue', v)}
                disabled={!settings?.notifications_enabled}
              />
            </div>
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="font-medium">Expiração de Ativos</p>
                <p className="text-sm text-slate-500">Alertar sobre fim de suporte/vida útil</p>
              </div>
              <Switch
                checked={settings?.notify_asset_expiry}
                onCheckedChange={(v) => updateField('notify_asset_expiry', v)}
                disabled={!settings?.notifications_enabled}
              />
            </div>
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="font-medium">Pedidos de Aprovação</p>
                <p className="text-sm text-slate-500">Notificar novos pedidos de aprovação</p>
              </div>
              <Switch
                checked={settings?.notify_approval_requests}
                onCheckedChange={(v) => updateField('notify_approval_requests', v)}
                disabled={!settings?.notifications_enabled}
              />
            </div>
          </CardContent>
        </Card>

        {/* Asset Lifecycle */}
        <Card className="card-flat">
          <CardHeader>
            <CardTitle className="text-lg">Ciclo de Vida de Ativos</CardTitle>
            <CardDescription>Configurar antecedência dos alertas de expiração</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Alerta de Fim de Vida (dias antes)</Label>
                <Input
                  type="number"
                  value={settings?.asset_expiry_warning_days || 30}
                  onChange={(e) => updateField('asset_expiry_warning_days', parseInt(e.target.value))}
                  min="1"
                  max="365"
                />
              </div>
              <div className="space-y-2">
                <Label>Alerta de Fim de Suporte (dias antes)</Label>
                <Input
                  type="number"
                  value={settings?.support_expiry_warning_days || 60}
                  onChange={(e) => updateField('support_expiry_warning_days', parseInt(e.target.value))}
                  min="1"
                  max="365"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={isSaving} size="lg" data-testid="save-settings-btn">
            {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Guardar Definições
          </Button>
        </div>

        {/* Privaxia Bridge */}
        <Card className="card-flat" data-testid="privaxia-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Zap className="h-5 w-5 text-purple-600" />
              Ligação Privaxia (Sincronização DPO)
            </CardTitle>
            <CardDescription>
              Sincronize DPIA e ROPA bidireccionalmente com a sua plataforma Privaxia. Requer URL da API, chave e workspace ID fornecidos pela Privaxia.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="privaxia_enabled"
                checked={!!settings?.privaxia_enabled}
                onChange={(e) => updateField('privaxia_enabled', e.target.checked)}
                data-testid="privaxia-enabled-toggle"
              />
              <Label htmlFor="privaxia_enabled" className="cursor-pointer">Activar ligação Privaxia</Label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>URL da API</Label>
                <Input
                  value={settings?.privaxia_api_url || ''}
                  onChange={(e) => updateField('privaxia_api_url', e.target.value)}
                  placeholder="https://api.privaxia.pt"
                  data-testid="privaxia-url-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Workspace ID</Label>
                <Input
                  value={settings?.privaxia_workspace_id || ''}
                  onChange={(e) => updateField('privaxia_workspace_id', e.target.value)}
                  placeholder="ws_abc123"
                  data-testid="privaxia-workspace-input"
                />
              </div>
              <div className="space-y-2">
                <Label>API Key</Label>
                <Input
                  type="password"
                  value={settings?.privaxia_api_key || ''}
                  onChange={(e) => updateField('privaxia_api_key', e.target.value)}
                  placeholder="Cole aqui o token fornecido pela Privaxia"
                  data-testid="privaxia-key-input"
                />
                <p className="text-[11px] text-slate-500">Guardado encriptado. Exibido mascarado após guardar.</p>
              </div>
              <div className="space-y-2">
                <Label>Webhook Secret</Label>
                <Input
                  type="password"
                  value={settings?.privaxia_webhook_secret || ''}
                  onChange={(e) => updateField('privaxia_webhook_secret', e.target.value)}
                  placeholder="HMAC secret para validar webhooks"
                  data-testid="privaxia-webhook-input"
                />
                <p className="text-[11px] text-slate-500">
                  Configure este mesmo secret na plataforma Privaxia para o endpoint <code className="bg-slate-100 px-1 rounded">POST /api/privaxia/webhook</code>.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* NIS2AI Verified Seal */}
        <Card data-testid="tenant-seal-card">          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Award className="h-5 w-5 text-indigo-600" />
              Selo NIS2AI Verified
            </CardTitle>
            <CardDescription>
              Exporte um selo de conformidade para o website da sua empresa. Reflete a % de conformidade atual (NIS2 + AI Act) e liga ao Trust Center público.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!seal ? (
              <Button
                variant="outline"
                onClick={async () => {
                  setLoadingSeal(true);
                  try {
                    const { data } = await api.get('/settings/tenant-seal');
                    setSeal(data);
                  } catch (err) { toast.error(err?.response?.data?.detail || 'Falha.'); }
                  finally { setLoadingSeal(false); }
                }}
                disabled={loadingSeal}
                data-testid="tenant-seal-gen-btn"
              >
                {loadingSeal ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Award className="h-4 w-4 mr-2" />} Gerar selo
              </Button>
            ) : (
              <>
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="text-xs text-slate-500 uppercase tracking-wide font-medium">Score atual</div>
                      <div className="text-2xl font-bold text-slate-900 tabular-nums" data-testid="tenant-seal-score">
                        {seal.status.score}%
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 text-right">
                      <div>NIS2: <strong>{seal.status.nis2_pct}%</strong></div>
                      <div>AI Act: <strong>{seal.status.ai_act_pct}%</strong></div>
                    </div>
                  </div>
                  {seal.status.score === 0 && (
                    <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2 mt-2">
                      💡 Complete os requisitos NIS2 e classifique sistemas IA para ter um score real. O selo pode ser gerado agora mas mostrará 0%.
                    </div>
                  )}
                  <div className="text-xs text-slate-500 mt-2">Emitido em {new Date(seal.issued_at).toLocaleDateString('pt-PT')} · Assinatura: <code className="bg-white px-1.5 py-0.5 rounded border border-slate-200">{seal.signature}</code></div>
                </div>
                <div className="space-y-3">
                  {[
                    { key: 'html', label: 'HTML (recomendado)' },
                    { key: 'markdown', label: 'Markdown' },
                    { key: 'iframe', label: 'iframe' },
                  ].map(s => (
                    <div key={s.key}>
                      <div className="flex items-center justify-between mb-1">
                        <Label className="text-xs uppercase tracking-wide text-slate-500">{s.label}</Label>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 text-xs"
                          onClick={() => { navigator.clipboard.writeText(seal.snippets[s.key]); toast.success('Copiado.'); }}
                          data-testid={`tenant-seal-copy-${s.key}`}
                        >
                          <Copy className="h-3 w-3 mr-1" /> Copiar
                        </Button>
                      </div>
                      <textarea
                        readOnly
                        value={seal.snippets[s.key]}
                        className="w-full text-xs font-mono bg-slate-900 text-emerald-400 rounded-lg p-3 border border-slate-700 h-20"
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
